"use client";

import { useTranslations } from "next-intl";
import { QUIET_HOVER } from "@/components/creatorPortal/one/chrome";
import { Textarea } from "@/components/ui/design-system/textarea";
import { cn } from "@/lib/utils";
import {
  type ApplyFormErrorKey,
  type ApplyFormState,
  MAX_ABOUT_LENGTH,
  MAX_INTEREST_AREAS,
} from "./applyForm";
import { Field, FieldError } from "./applyFormPrimitives";

export function ApplyInterestsStep({
  form,
  interestAreas,
  errorFor,
  onChange,
  onToggleArea,
}: {
  form: ApplyFormState;
  interestAreas: readonly string[];
  errorFor: (key: ApplyFormErrorKey) => string | undefined;
  onChange: <K extends keyof ApplyFormState>(
    key: K,
    value: ApplyFormState[K],
  ) => void;
  onToggleArea: (area: string) => void;
}) {
  const t = useTranslations("apply");
  return (
    <div>
      <div className="flex flex-wrap gap-nf-ds-2">
        {interestAreas.map((area) => {
          const selected = form.interestAreas.includes(area);
          // Disabling the unpicked chips at the cap says "you're done here"
          // more clearly than silently ignoring the tap would.
          const atCap =
            !selected && form.interestAreas.length >= MAX_INTEREST_AREAS;
          return (
            <button
              key={area}
              type="button"
              onClick={() => onToggleArea(area)}
              disabled={atCap}
              aria-pressed={selected}
              className={cn(
                "cursor-pointer rounded-nf-ds-pill px-nf-ds-4 py-nf-ds-2 font-nf-ds-sans text-nf-ds-label",
                // A chip is pressed often enough to want feedback and rarely
                // enough that a short one costs nothing.
                "transition-[color,background-color,scale] duration-nf-ds-1 ease-nf-ds-out active:scale-97 motion-reduce:active:scale-100",
                selected
                  ? "bg-nf-ds-brand text-nf-ds-brand-fg"
                  : cn(
                      "text-nf-ds-fg ring-1 ring-inset ring-nf-ds-surface-card-ring",
                      QUIET_HOVER,
                    ),
                atCap && "cursor-not-allowed opacity-40",
              )}
            >
              {area}
            </button>
          );
        })}
      </div>
      <FieldError message={errorFor("interestAreas")} />

      <div className="mt-nf-ds-6">
        <Field
          label={t("fields.about.label")}
          hint={t("fields.about.hint", {
            count: MAX_ABOUT_LENGTH - form.aboutYou.length,
          })}
        >
          <Textarea
            value={form.aboutYou}
            maxLength={MAX_ABOUT_LENGTH}
            onChange={(e) => onChange("aboutYou", e.target.value)}
            placeholder={t("fields.about.placeholder")}
            className="min-h-nf-ds-32"
          />
        </Field>
      </div>
    </div>
  );
}
